// frontend/src/components/services-orbit/OrbitServicesSlider.jsx
import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { gsap } from "gsap";
import OrbitTile from "./OrbitTile";
import {
  computeGalleryLayout,
  getFrontIndex,
  computeTargetRotationForIndex,
} from "./orbitGeometry";

const ORBIT_CONTAINER_HEIGHT = 520;
// Each tile now renders as a full card (icon + title + description), not a
// small orb — this is also the card's rendered CSS width, so it drives both
// orbit spacing (via orbitGeometry) and the actual on-screen card size.
const TILE_SIZE = 260;
const DEFAULT_ROTATE_SPEED_DEG_PER_SEC = 6;
const DEFAULT_JUMP_DURATION_SEC = 0.6;
// Orbit radius is directly proportional to tileSize (see orbitGeometry.js),
// so shrinking the effective tile size for narrow containers is what keeps
// the composition from overflowing on mobile. Swept the full 360deg
// rotation (not just a single snapshot) against real containerWidths
// before picking this divisor — a fixed tileSize alone still overflows a
// ~327px mobile container by ~38px at some rotation phases, even though a
// snapshot at any ONE moment can look fine. At desktop container widths the
// divisor comfortably clears TILE_SIZE (no-op); at typical tablet widths it
// now also engages, proportionally shrinking the cards so a 5-card ring
// still fits without overflowing a narrower container.
export function getEffectiveTileSize(containerWidth) {
  if (!containerWidth) return TILE_SIZE;
  return Math.min(TILE_SIZE, containerWidth / 4.2);
}

export default function OrbitServicesSlider({
  services,
  onActiveIndexChange = () => {},
  rotateSpeedDegPerSec = DEFAULT_ROTATE_SPEED_DEG_PER_SEC,
  jumpDurationSec = DEFAULT_JUMP_DURATION_SEC,
}) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [tiles, setTiles] = useState(() =>
    computeGalleryLayout({
      count: services.length,
      rotationOffsetDeg: 0,
      containerWidth: 0,
      containerHeight: ORBIT_CONTAINER_HEIGHT,
      tileSize: getEffectiveTileSize(0),
    })
  );

  const rotationStateRef = useRef({ deg: 0 });
  const autoRotateTweenRef = useRef(null);
  const jumpTweenRef = useRef(null);
  const ctxRef = useRef(null);
  const isHoveringRef = useRef(false);
  // Whether the orbit is currently anywhere in the viewport — gates the
  // continuous auto-rotate tween so it doesn't keep re-rendering 5 tiles
  // at 60fps (and re-evaluating two matchMedia queries per tick) while the
  // section is scrolled out of view. GSAP's own ticker already throttles
  // when the whole TAB is hidden, but not when this one element is merely
  // off-screen within a visible tab.
  const isInViewRef = useRef(true);
  // -1 (not 0) is the "nothing reported yet" sentinel — 0 is a legitimate
  // front-tile index, and using it as the sentinel would silently suppress
  // the initial onActiveIndexChange call whenever the real front tile
  // happens to be index 0 (verified: happens for 1-2 service arrays with
  // rotationOffsetDeg=0).
  const lastFrontIndexRef = useRef(-1);
  const onActiveIndexChangeRef = useRef(onActiveIndexChange);
  onActiveIndexChangeRef.current = onActiveIndexChange;

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const supportsHover =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  // useLayoutEffect (not useEffect) so the real width is measured and
  // committed before the browser paints — otherwise the first frame
  // renders with containerWidth still 0, flashing tiles clustered near
  // the left edge before they snap into their correctly centered layout.
  useLayoutEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.getBoundingClientRect().width);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const recomputeAndCommit = useCallback(() => {
    const layout = computeGalleryLayout({
      count: services.length,
      rotationOffsetDeg: rotationStateRef.current.deg,
      containerWidth,
      containerHeight: ORBIT_CONTAINER_HEIGHT,
      tileSize: getEffectiveTileSize(containerWidth),
    });
    setTiles(layout);
    const front = getFrontIndex(layout);
    if (front !== lastFrontIndexRef.current) {
      lastFrontIndexRef.current = front;
      onActiveIndexChangeRef.current(front);
    }
  }, [services.length, containerWidth]);

  // Also useLayoutEffect, for the same pre-paint reason as the measurement
  // effect above — this is what actually turns the real measured width
  // into a committed `tiles` layout before the browser paints.
  useLayoutEffect(() => {
    recomputeAndCommit();
  }, [recomputeAndCommit]);

  // recomputeAndCommit's identity changes whenever containerWidth changes
  // (every resize). The auto-rotate tween's onUpdate must NOT close over
  // that changing identity directly — if it did, rotateSpeedDegPerSec's
  // useCallback below would need recomputeAndCommit in its own deps too,
  // which would tear down and recreate the whole gsap.context() (see the
  // mount effect) on every resize. Reverting a context does not just stop
  // its tweens — gsap.context().revert() resets the target object's
  // property back to its pre-tween value (verified empirically against
  // this repo's installed gsap version: a tween mid-rotation at 144deg
  // read back as 0deg immediately after ctx.revert()). That means every
  // resize was snapping the orbit's rotation back to its mount position.
  // Routing onUpdate through a ref that's refreshed every render — the
  // same pattern already used for onActiveIndexChangeRef above — lets
  // startAutoRotate stay stable across resizes while still always calling
  // the current recomputeAndCommit closure.
  const recomputeAndCommitRef = useRef(recomputeAndCommit);
  recomputeAndCommitRef.current = recomputeAndCommit;

  const startAutoRotate = useCallback(() => {
    return gsap.to(rotationStateRef.current, {
      deg: "+=360",
      duration: 360 / rotateSpeedDegPerSec,
      ease: "none",
      repeat: -1,
      onUpdate: () => recomputeAndCommitRef.current(),
    });
  }, [rotateSpeedDegPerSec]);

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const ctx = gsap.context(() => {
      autoRotateTweenRef.current = startAutoRotate();
    });
    ctxRef.current = ctx;

    return () => {
      // Kill any in-flight tap-to-jump tween too — it was created outside
      // this ctx (see handleTileClick below), so ctx.revert() alone
      // wouldn't catch it.
      jumpTweenRef.current?.kill();
      ctx.revert();
      autoRotateTweenRef.current = null;
      ctxRef.current = null;
    };
  }, [prefersReducedMotion, startAutoRotate]);

  // Single source of truth for "should the auto-rotate tween be paused
  // right now" — both the hover handlers and the visibility observer feed
  // into this instead of each independently calling .pause()/.resume(),
  // so (for example) the pointer leaving the element doesn't incorrectly
  // resume rotation while the element is still scrolled out of view.
  const updateAutoRotatePause = () => {
    const shouldPause = (supportsHover && isHoveringRef.current) || !isInViewRef.current;
    if (shouldPause) {
      autoRotateTweenRef.current?.pause();
    } else {
      autoRotateTweenRef.current?.resume();
    }
  };

  // Pauses the continuous auto-rotate tween while the orbit is scrolled
  // out of view. Desktop/pointer-fine hover already pauses it (below);
  // this covers the "off-screen in an otherwise-visible tab" case that
  // GSAP's own tab-visibility-aware ticker throttling doesn't reach.
  useEffect(() => {
    if (prefersReducedMotion) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isInViewRef.current = entry.isIntersecting;
        updateAutoRotatePause();
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // Deliberately keyed on prefersReducedMotion alone: updateAutoRotatePause
    // is re-created every render but only ever reads refs, so re-creating the
    // observer for it would churn for no behavioural difference. (Written as
    // a plain comment rather than an eslint-disable directive — this app's
    // CRA ESLint config doesn't load the react-hooks plugin, and a disable
    // comment naming a rule it can't resolve fails the production build.)
  }, [prefersReducedMotion]);

  const handleMouseEnter = () => {
    if (!supportsHover) return;
    isHoveringRef.current = true;
    updateAutoRotatePause();
  };
  const handleMouseLeave = () => {
    if (!supportsHover) return;
    isHoveringRef.current = false;
    updateAutoRotatePause();
  };

  const handleTileClick = useCallback(
    (index) => {
      const target = computeTargetRotationForIndex(
        index,
        services.length,
        rotationStateRef.current.deg
      );

      if (prefersReducedMotion) {
        rotationStateRef.current.deg = target;
        recomputeAndCommitRef.current();
        return;
      }

      // GSAP does not automatically overwrite a running tween on the same
      // target/property (verified against this repo's installed version —
      // see Global Constraints Gotcha 1) — kill it explicitly first, and
      // start a brand new auto-rotate tween in onComplete (not `.resume()`
      // the old instance) so rotation continues from the jump's landing
      // value instead of snapping back to the old tween's stale position.
      autoRotateTweenRef.current?.kill();
      jumpTweenRef.current?.kill();
      jumpTweenRef.current = gsap.to(rotationStateRef.current, {
        deg: target,
        duration: jumpDurationSec,
        ease: "power3.out",
        onUpdate: () => recomputeAndCommitRef.current(),
        onComplete: () => {
          // A tween created inside a plain onComplete callback runs after
          // the mount effect's gsap.context() synchronous callback has
          // already returned, so GSAP never tracks it under that context —
          // verified empirically: such a tween survives ctx.revert()
          // untouched and keeps ticking after unmount. ctx.add() re-enters
          // the context's tracking (its callback runs synchronously), so
          // the new auto-rotate tween is properly killed by unmount cleanup.
          ctxRef.current?.add(() => {
            autoRotateTweenRef.current = startAutoRotate();
            updateAutoRotatePause();
          });
        },
      });
    },
    [prefersReducedMotion, services.length, jumpDurationSec, startAutoRotate]
  );

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: "relative",
        width: "100%",
        height: ORBIT_CONTAINER_HEIGHT,
        backgroundColor: "transparent",
        overflow: "visible",
      }}
      data-testid="orbit-services-slider"
    >
      {/*
        :focus-visible (not a JS onFocus/onBlur ring) so the highlight only
        appears for real keyboard navigation — a mouse click also focuses
        the element, and a JS-driven ring would keep glowing on that tile
        after the orbit rotates it away from front. !important is required:
        inline styles normally beat stylesheet rules, but a stylesheet rule
        marked !important beats a plain inline style.

        The @media block below is the ONLY mobile-specific override in this
        component — desktop (>767px, matching this codebase's other mobile
        breakpoints) keeps the full expand-on-active behavior untouched.
        On mobile, cards never expand on focus: only the icon badge and
        title show, description/tags stay permanently collapsed via
        !important (needed to beat OrbitTile's inline isActive-driven
        maxHeight/opacity). They're still mounted in the DOM (screen
        readers/crawlers still reach them) — just never visually shown at
        this width, matching how they're hidden pre-activation everywhere.
      */}
      <style>{`
        .orbit-tile-btn:focus-visible > div {
          box-shadow: 0 0 0 3px rgba(194,232,225,0.9), 0 8px 24px rgba(0,0,0,0.28) !important;
        }
        @media (max-width: 767px) {
          .orbit-tile-desc, .orbit-tile-tags {
            max-height: 0 !important;
            opacity: 0 !important;
            margin: 0 !important;
            overflow: hidden !important;
          }
        }
      `}</style>
      {services.map((service, index) => (
        <OrbitTile
          key={service.title}
          title={service.title}
          mobileTitle={service.titleMobile}
          desc={service.desc}
          related={service.related}
          Icon={service.Icon}
          size={getEffectiveTileSize(containerWidth)}
          x={tiles[index]?.x ?? 0}
          y={tiles[index]?.y ?? 0}
          scale={tiles[index]?.scale ?? 1}
          zIndex={tiles[index]?.zIndex ?? 1}
          isActive={index === lastFrontIndexRef.current}
          hoverEnabled={supportsHover && !prefersReducedMotion}
          onClick={() => handleTileClick(index)}
        />
      ))}
    </div>
  );
}
